import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Box,
  TextField,
  useTheme,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import PersistentDataGrid from "../../components/table/PersistentDataGrid";
import { GridToolbar } from "@mui/x-data-grid";
import { faEllipsisV } from "@fortawesome/free-solid-svg-icons/faEllipsisV";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";
import InventoryServices from "../../services/InventoryServices";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setShowMessage } from "../../redux/slices/notificationSlice";
import translations from "../../assets/locals/translations";

const ModelStock = () => {
  const InventoryService = new InventoryServices();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const theme = useTheme();

  // Redux & Translation Setup
  const language = useSelector((state) => state.language?.language || "en");
  const t = translations[language] || translations["en"];

  // States
  const [stockData, setStockData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(25);
  const [searchQuery, setSearchQuery] = useState("");
  const [modelDepoColumnShowHideModel, setModelDepoColumnShowHideModel] = useState({});

  // Responsiveness
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // --- Effects ---

  // Load column visibility preferences
  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem('modelDepo_columnShowHide'));
    if (saved) {
      setModelDepoColumnShowHideModel(saved);
    }
  }, []);

  // Fetch Data
  const getStockLogs = async () => {
    setLoading(true);
    try {
      const response = await InventoryService.getStockLogs("IN_DEPO");
      if (response && response.isSuccess) {
        const rowData = response.result.map((item, index) => ({
          ...item,
          bil: index + 1,
        }));
        setStockData(rowData);
      }
    } catch (error) {
      console.error(error, "Error getting stocks list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getStockLogs();
  }, []);

  // --- Handlers ---

  const handleModelDepoColumnShowHideChange = (newModel) => {
    setModelDepoColumnShowHideModel(newModel);
    localStorage.setItem('modelDepo_columnShowHide', JSON.stringify(newModel));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
  };

  const addDelivery = async (row) => {
    navigate("/depo/delivery/add", {
      state: { stockData: row, ev_stage: { type: "DELIVERY", value: 2 } },
    });
  };

  const handleSampleDownload = async () => {
    try {
      const response = await InventoryService.downloadSampleStocks();
      if (response && response.data) {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "Stocks_Sample.xlsx");
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
      }
    } catch (error) {
      console.error("Error downloading sample:", error);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    setLoading(true);
    try {
      const response = await InventoryService.uploadStocksExcel(formData);
      if (response && response.isSuccess) {
        dispatch(setShowMessage("Upload successful!"));
        await getStockLogs();
      } else {
        dispatch(setShowMessage(response?.message || "Upload failed"));
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      dispatch(setShowMessage("Error uploading file"));
    } finally {
      setLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // --- Data Grid Configuration ---

  const columns = [
    {
      field: "menu",
      headerName: t.menu || "Menu",
      className: "bg-dark",
      sortable: false,
      filterable: false,
      headerAlign: "left",
      align: "left",
      disableColumnMenu: true,
      width: 50,
      renderCell: (params) => (
        <Tooltip title={t.addDelivery || "Add Delivery"}>
          <button
            onClick={() => addDelivery(params.row)}
            className="myActionBtn d-flex align-items-center"
            type="button"
            style={{ cursor: "pointer" }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="22"
              height="22"
              fill="currentColor"
              className="bi bi-plus-circle text-primary"
              viewBox="0 0 16 16"
            >
              <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
              <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z" />
            </svg>
          </button>
        </Tooltip>
      ),
    },
    { field: "bil", headerName: t.stock || "STOCK", flex: 1, minWidth: 80 },
    { field: "bil_no", headerName: t.bilNo || "BIL NO", flex: 1, minWidth: 150 },
    {
      field: "date",
      headerName: t.date || "DATE",
      flex: 1,
      minWidth: 150,
      valueFormatter: (params) => moment(params.value).format("DD-MM-YYYY"),
    },
    { field: "bum_cod", headerName: t.bumCod || "BUM COD", flex: 1, minWidth: 100 },
    { field: "model_name", headerName: t.modal || "MODAL", flex: 1, minWidth: 100 },
    { field: "chassis", headerName: t.chassis || "CHASSIS", flex: 1, minWidth: 200 },
    { field: "engine", headerName: t.engine || "ENGINE", flex: 1, minWidth: 200 },
    { field: "ndp", headerName: t.ndp || "NDP", flex: 1, minWidth: 120 },
    { field: "location", headerName: t.location || "Location", flex: 1, minWidth: 100 },
    { field: "number22", headerName: t.number22 || "NUMBER22", flex: 1, minWidth: 120 },
    {
      field: "status",
      headerName: t.status || "Status",
      flex: 1,
      minWidth: 100,
      renderCell: (params) => (
        <button
          className="btn btn-sm m-1 text-success"
          style={{ backgroundColor: "#d4edda" }}
        >
          {t.inDepo || "In Depo"}
        </button>
      ),
    },
  ];

  // Filter rows based on search query
  const filteredRows = useMemo(() => {
    if (!searchQuery) return stockData;
    return stockData.filter((row) =>
      Object.keys(row).some((field) =>
        String(row[field]).toLowerCase().includes(searchQuery.toLowerCase())
      )
    );
  }, [searchQuery, stockData]);

  return (
    <div className="bg-white p-2">
      {/* Header: search left, Sample & Upload buttons right */}
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: isMobile ? "stretch" : "center",
          justifyContent: "space-between",
          gap: 1,
          mb: 1,
        }}
      >
        <TextField
          variant="outlined"
          placeholder={t.globalSearch || "Global Search..."}
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ width: isMobile ? "100%" : 300, flexShrink: 0 }}
        />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
            justifyContent: isMobile ? "flex-start" : "flex-end",
            marginLeft: isMobile ? 0 : "auto",
          }}
        >
          <button
            className="btn btn-outline-info d-flex align-items-center"
            onClick={handleSampleDownload}
            title="Download Sample Stocks Excel"
          >
            {t.sample || "Sample"}
          </button>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            accept=".xlsx, .xls"
            onChange={handleFileUpload}
          />
          <button
            className="btn btn-outline-success d-flex align-items-center"
            onClick={() => fileInputRef.current?.click()}
            title="Upload Excel File"
          >
            {t.upload || "Upload"}
          </button>
        </Box>
      </Box>

      <div
        className="tableMenuHover"
        style={{ flexGrow: 1, width: "100%", height: "calc(100vh - 70px)" }}
      >
        <PersistentDataGrid
          rows={filteredRows}
          columns={columns}
          columnVisibilityModel={modelDepoColumnShowHideModel}
          onColumnVisibilityModelChange={handleModelDepoColumnShowHideChange}
          getRowId={(row) => row.bil}
          pagination
          pageSize={pageSize}
          pageSizeOptions={[10, 15, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 50 } } }}
          rowsPerPageOptions={[10, 20, 25, 50, 100]}
          onPageSizeChange={handlePageSizeChange}
          disableSelectionOnClick
          autoHeight={isMobile}
          components={{
            Toolbar: GridToolbar,
            NoRowsOverlay: () => (
              <div
                style={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                }}
              >
                <span style={{ fontFamily: "Poppins" }}>
                  {t.noDataAvailable || "No Data Available"}
                </span>
              </div>
            ),
          }}
          componentsProps={{
            toolbar: {
              position: "right",
              style: { fontFamily: "Poppins", alignSelf: "end" },
            },
          }}
          sx={{ width: "100%" }}
          loading={loading}
        />
      </div>
    </div>
  );
};

export default ModelStock;